import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useActiveTenantId } from '@/store/tenantStore'
import {
  Badge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@biartbt/biartd'
import { CrudPage } from '@/components/Crud'
import type { Department } from '../api/departmentsApi'
import { departmentsApi } from '../api/departmentsApi'
import { organizationsApi } from '@/modules/system/organizations/api/organizationsApi'
import type { Organization } from '@/modules/system/organizations/api/organizationsApi'

type DeptForm = {
  code: string
  name: string
  description: string
  organizationId: string
  parentId: string
  isActive: boolean
}

function emptyToNull(s: string): string | null {
  const t = s?.trim()
  return !t ? null : t
}

export default function DepartmentsPage() {
  const [searchParams] = useSearchParams()
  const activeTenantId = useActiveTenantId()
  const tenantIdFromUrl = searchParams.get('tenantId')
  const tenantId = tenantIdFromUrl ? Number(tenantIdFromUrl) : activeTenantId
  const { toast } = useToast()

  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [departments, setDepartments] = useState<Department[]>([])
  const [lookupsLoading, setLookupsLoading] = useState(false)

  const loadLookups = useCallback(async () => {
    if (!tenantId) return
    setLookupsLoading(true)
    try {
      const [orgRes, deptRes] = await Promise.all([
        organizationsApi.list({ tenantId, pageSize: 500, isActive: true }),
        departmentsApi.list({ tenantId, pageSize: 500, isActive: true }),
      ])
      setOrganizations(orgRes.items)
      setDepartments(deptRes.items)
    } catch {
      setOrganizations([])
      setDepartments([])
    } finally {
      setLookupsLoading(false)
    }
  }, [tenantId])

  useEffect(() => {
    loadLookups()
  }, [loadLookups])

  const parentOptions = (excludeId?: number) =>
    departments
      .filter((d) => d.departmentId !== excludeId)
      .map((d) => ({ value: String(d.departmentId), label: `${d.code ?? ''} - ${d.name}`.trim() || d.name }))

  const orgOptions = organizations.map((o) => ({
    value: String(o.organizationId),
    label: `${o.code ?? ''} - ${o.name}`.trim() || o.name,
  }))

  return (
    <CrudPage<Department, DeptForm, any, any>
      title="Departmanlar"
      subtitle="HR departmanlarını yönet"
      primaryActionLabel="Yeni Departman"
      onExportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de export endpoint'i yok. TODO: GET /api/v1/departments/export (Reporting).",
          variant: 'warning',
        })
      }
      onImportClick={() =>
        toast({
          title: 'Eksik',
          description: "Backend'de import endpoint'i yok. TODO: POST /api/v1/departments/import (HR write).",
          variant: 'warning',
        })
      }
      list={async (p) =>
        departmentsApi.list({
          tenantId,
          search: p.search,
          page: p.page,
          pageSize: p.pageSize,
          sortBy: (p.sortBy as any) ?? undefined,
          sortDir: (p.sortDir as any) ?? undefined,
          isActive: p.filters.status === 'all' ? null : p.filters.status === 'active',
          includeDeleted: p.filters.deleted === 'show',
        })
      }
      getRowId={(d) => d.departmentId}
      columns={[
        {
          key: 'code',
          header: 'Kod',
          sortable: true,
          widthClassName: 'w-[120px]',
          render: (d) => <span className="font-medium">{d.code ?? '-'}</span>,
        },
        {
          key: 'name',
          header: 'Ad',
          sortable: true,
          widthClassName: 'w-[220px]',
          render: (d) => d.name,
        },
        {
          key: 'description',
          header: 'Açıklama',
          sortable: false,
          render: (d) => <span className="truncate block max-w-[320px]">{d.description ?? '-'}</span>,
        },
        {
          key: 'parentId',
          header: 'Üst Departman',
          sortable: false,
          widthClassName: 'w-[160px]',
          render: (d) => {
            if (!d.parentId) return '-'
            const parent = departments.find((p) => p.departmentId === d.parentId)
            return parent?.name ?? String(d.parentId)
          },
        },
        {
          key: 'isActive',
          header: 'Durum',
          sortable: false,
          widthClassName: 'w-[100px]',
          render: (d) =>
            d.isActive ? <Badge variant="success">Aktif</Badge> : <Badge variant="warning">Pasif</Badge>,
        },
      ]}
      searchPlaceholder="Ara (kod, ad, açıklama)…"
      filters={[
        {
          id: 'status',
          label: 'Durum',
          value: 'all',
          options: [
            { label: 'Tümü', value: 'all' },
            { label: 'Aktif', value: 'active' },
            { label: 'Pasif', value: 'inactive' },
          ],
        },
        {
          id: 'deleted',
          label: 'Silinenler',
          value: 'hide',
          options: [
            { label: 'Gizle', value: 'hide' },
            { label: 'Göster', value: 'show' },
          ],
          widthClassName: 'min-w-[160px]',
        },
      ]}
      initialPageSize={10}
      modalTitles={{ create: 'Yeni Departman', edit: 'Departmanı Düzenle' }}
      getCreateDefaultForm={() => ({
        code: '',
        name: '',
        description: '',
        organizationId: '',
        parentId: '',
        isActive: true,
      })}
      getEditForm={(d) => ({
        code: d.code ?? '',
        name: d.name ?? '',
        description: d.description ?? '',
        organizationId: d.organizationId != null ? String(d.organizationId) : '',
        parentId: d.parentId != null ? String(d.parentId) : '',
        isActive: d.isActive,
      })}
      validate={(f) => ({
        code: f.code.trim().length > 50 ? 'Kod en fazla 50 karakter olmalıdır' : undefined,
        name: !f.name.trim() ? 'Departman adı zorunludur' : f.name.trim().length > 255 ? 'Ad en fazla 255 karakter olmalıdır' : undefined,
        description: f.description.trim().length > 1000 ? 'Açıklama en fazla 1000 karakter olmalıdır' : undefined,
      })}
      renderForm={({ form, setForm, touched, setTouched, errors, isSubmitting, mode, item }) => (
        <div className="grid gap-4">
          <Input
            label="Kod"
            value={form.code}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, code: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.code}
            helperText={touched ? errors.code : undefined}
            disabled={isSubmitting}
            placeholder="Departman kodu"
            leftIcon={<i className="material-symbols-outlined text-lg">tag</i>}
          />
          <Input
            label="Departman Adı"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm((s) => ({ ...s, name: e.target.value }))}
            onBlur={() => setTouched(true)}
            error={touched && !!errors.name}
            helperText={touched ? errors.name : undefined}
            required
            disabled={isSubmitting}
            placeholder="Departman adı"
            leftIcon={<i className="material-symbols-outlined text-lg">business</i>}
          />
          <Textarea
            label="Açıklama"
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              setForm((s) => ({ ...s, description: e.target.value }))
            }
            onBlur={() => setTouched(true)}
            error={touched && !!errors.description}
            helperText={touched ? errors.description : undefined}
            disabled={isSubmitting}
            placeholder="Departman açıklaması"
          />
          <div>
            <label className="block text-sm font-medium mb-1.5">Organizasyon Birimi</label>
            <Select
              value={form.organizationId ? String(form.organizationId) : '__none__'}
              onValueChange={(v: string) => setForm((s) => ({ ...s, organizationId: v === '__none__' ? '' : v }))}
              disabled={isSubmitting || lookupsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Organizasyon seçin (opsiyonel)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Seçin —</SelectItem>
                {orgOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1.5">Üst Departman</label>
            <Select
              value={form.parentId ? String(form.parentId) : '__none__'}
              onValueChange={(v: string) => setForm((s) => ({ ...s, parentId: v === '__none__' ? '' : v }))}
              disabled={isSubmitting || lookupsLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Üst departman seçin (opsiyonel)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Yok —</SelectItem>
                {parentOptions(mode === 'edit' && item ? (item as Department).departmentId : undefined).map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Switch
            checked={form.isActive}
            onCheckedChange={(v: boolean) => setForm((s) => ({ ...s, isActive: v }))}
            label="Aktif"
            disabled={isSubmitting}
          />
        </div>
      )}
      buildCreatePayload={(f) => ({
        tenantId,
        code: emptyToNull(f.code),
        name: f.name.trim(),
        description: emptyToNull(f.description),
        organizationId: f.organizationId ? Number(f.organizationId) : null,
        parentId: f.parentId ? Number(f.parentId) : null,
        isActive: f.isActive,
      })}
      buildUpdatePayload={(f, item) => ({
        tenantId,
        code: emptyToNull(f.code),
        name: f.name.trim(),
        description: emptyToNull(f.description),
        organizationId: f.organizationId ? Number(f.organizationId) : null,
        parentId: f.parentId ? Number(f.parentId) : null,
        isActive: f.isActive,
      })}
      create={(payload) => departmentsApi.create(payload)}
      update={(id, payload) => departmentsApi.update({ id: Number(id), ...(payload as any) })}
      remove={(id) => departmentsApi.delete(Number(id))}
      getCreatedId={(res) => res?.data?.id}
    />
  )
}
